import React from 'react';
import { Alert, AlertTitle, Box, Snackbar, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { credentialsRevokedAlertDismissed } from '../../store/slices/safetySlice';
import { formatTimestamp } from '../../utils/formatters';

/**
 * A drone was cut off because its refresh token was used twice (#777, C8).
 *
 * A refresh token is spent once; one spent again was copied, so the server
 * revokes every token of the drone, the copy and the drone's own alike. The
 * drone then cannot reach the fleet at all, so it cannot say so: this is the
 * only place its pilot hears of it. It stays until dismissed, and says what to
 * do, which differs by kind of drone.
 */

/** What the pilot does to give the drone new credentials. */
const recovery = (droneId: string, virtual: boolean): string =>
  virtual
    ? 'A virtual drone cannot be given new credentials: delete it and create another.'
    : `Give it new credentials: fleetctl install -ssh USER@HOST -drone-id ${droneId}.`;

export const CredentialsRevokedAlerts: React.FC = () => {
  const dispatch = useDispatch();
  const alerts = useSelector((state: RootState) => state.safety.credentialsRevokedAlerts);
  const drones = useSelector((state: RootState) => state.drones.drones);

  if (alerts.length === 0) return null;

  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ maxWidth: { xs: '100%', sm: 480 } }}
    >
      <Box
        role="region"
        aria-label="Revoked drone credentials"
        sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}
      >
        {alerts.map((alert) => {
          const drone = drones[alert.droneId];
          return (
            <Alert
              key={alert.id}
              severity="error"
              role="alert"
              variant="filled"
              onClose={() => dispatch(credentialsRevokedAlertDismissed(alert.id))}
            >
              <AlertTitle>{drone?.name ?? alert.droneId} is cut off from the fleet</AlertTitle>
              <Typography variant="body2">
                Its refresh token was used twice at {formatTimestamp(alert.revokedAt)}, so it is
                treated as stolen and every token of the drone was revoked. If it is flying, it
                has lost its link and will return home. {recovery(alert.droneId, drone?.virtual ?? false)}
              </Typography>
            </Alert>
          );
        })}
      </Box>
    </Snackbar>
  );
};
